'use client';
import React from 'react';
import { X } from 'lucide-react';
import { BrowseFilters } from './BrowseContent';
import { BadgeTier } from '@/components/ui/BadgeTierChip';
import { VerificationStatus } from '@/components/ui/VerificationBadge';

interface FilterSidebarProps {
  filters: BrowseFilters;
  onChange: (f: BrowseFilters) => void;
}

const DOMAINS = ['FMCG', 'M&A', 'Due Diligence', 'Manufacturing', 'Supply Chain', 'Healthcare', 'Digital Transformation', 'Private Equity', 'Real Estate', 'D2C', 'Logistics', 'Financial Services', 'Brand Strategy', 'Capital Markets'];
const BADGE_TIERS: BadgeTier[] = ['VAULT', 'GOLD', 'SILVER', 'BRONZE', 'NONE'];
const BADGE_LABELS: Record<BadgeTier, string> = { VAULT: '◆◆◆◆ Vault', GOLD: '◆◆◆ Gold', SILVER: '◆◆ Silver', BRONZE: '◆ Bronze', NONE: 'Unrated' };
const VERIFICATION_STATUSES: VerificationStatus[] = ['BUYER_VERIFIED', 'WEB_CHECKED', 'PENDING'];
const VERIFICATION_LABELS: Record<VerificationStatus, string> = { BUYER_VERIFIED: 'Buyer Verified', WEB_CHECKED: 'Web Checked', PENDING: 'Pending', FLAGGED: 'Flagged' };
const EXPERIENCE_TIERS = ['EXPERT', 'PRINCIPAL', 'SENIOR'];

function toggleArray<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

function CheckboxRow({ checked, label, onChange }: { checked: boolean; label: string; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group">
      <div
        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all ${checked ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/40'}`}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={(e) => e.key === ' ' && onChange()}
      >
        {checked && <span className="text-primary-foreground text-[9px] font-bold">✓</span>}
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" aria-label={label} />
      <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors font-mono-data">{label}</span>
    </label>
  );
}

export default function FilterSidebar({ filters, onChange }: FilterSidebarProps) {
  const clearAll = () => onChange({ ...filters, domains: [], badgeTiers: [], verificationStatus: [], experienceTiers: [], rateMin: 0, rateMax: 100000 });
  const hasFilters = filters.domains.length + filters.badgeTiers.length + filters.verificationStatus.length + filters.experienceTiers.length > 0;

  return (
    <div className="surface-elevated p-4 flex flex-col gap-5 sticky top-24">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider">Filters</span>
        {hasFilters && (
          <button onClick={clearAll} className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors" aria-label="Clear all filters">
            <X size={10} />
            Clear all
          </button>
        )}
      </div>

      <div>
        <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">Domain</p>
        <div className="flex flex-col gap-2 max-h-48 overflow-y-auto scrollbar-thin pr-1">
          {DOMAINS.map((d) => (
            <CheckboxRow
              key={`filter-domain-${d}`}
              checked={filters.domains.includes(d)}
              label={d}
              onChange={() => onChange({ ...filters, domains: toggleArray(filters.domains, d) })}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">Badge Tier</p>
        <div className="flex flex-col gap-2">
          {BADGE_TIERS.map((tier) => (
            <CheckboxRow
              key={`filter-badge-${tier}`}
              checked={filters.badgeTiers.includes(tier)}
              label={BADGE_LABELS[tier]}
              onChange={() => onChange({ ...filters, badgeTiers: toggleArray(filters.badgeTiers, tier) })}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">
          Rate Range (₹/hr)
        </p>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={filters.rateMin}
              onChange={(e) => onChange({ ...filters, rateMin: Number(e.target.value) })}
              step="1000"
              min="0"
              className="flex-1 bg-input border border-border rounded px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 font-mono-data"
              placeholder="Min"
              aria-label="Minimum hourly rate"
            />
            <span className="text-muted-foreground text-xs">–</span>
            <input
              type="number"
              value={filters.rateMax}
              onChange={(e) => onChange({ ...filters, rateMax: Number(e.target.value) })}
              step="1000"
              min="0"
              className="flex-1 bg-input border border-border rounded px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary/50 font-mono-data"
              placeholder="Max"
              aria-label="Maximum hourly rate"
            />
          </div>
          <p className="text-[10px] text-muted-foreground font-mono-data">
            ₹{filters.rateMin.toLocaleString('en-IN')} – ₹{filters.rateMax.toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">Verification</p>
        <div className="flex flex-col gap-2">
          {VERIFICATION_STATUSES.map((vs) => (
            <CheckboxRow
              key={`filter-vs-${vs}`}
              checked={filters.verificationStatus.includes(vs)}
              label={VERIFICATION_LABELS[vs]}
              onChange={() => onChange({ ...filters, verificationStatus: toggleArray(filters.verificationStatus, vs) })}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-3">Experience Tier</p>
        <div className="flex flex-col gap-2">
          {EXPERIENCE_TIERS.map((et) => (
            <CheckboxRow
              key={`filter-et-${et}`}
              checked={filters.experienceTiers.includes(et)}
              label={et}
              onChange={() => onChange({ ...filters, experienceTiers: toggleArray(filters.experienceTiers, et) })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}